import { useEffect, useMemo, useState } from 'react'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Card } from '@magnetic/card'
import { Button } from '@magnetic/button'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Modal } from '@magnetic/modal'
import { Select } from '@magnetic/select'
import { Spinner } from '@magnetic/spinner'
import { Checkbox } from '@magnetic/switch'
import { Tag } from '@magnetic/tag'
import { Text } from '@magnetic/text'
import { Tooltip } from '@magnetic/tooltip'
import {
  Package,
  Calendar,
  Clock,
  Download,
  WarningCircle,
  ArrowsClockwise
} from '@/lib/icons'
import { configureTagTracking, deployImageTagRPC, listImageTags } from '@/lib/api'
import { invalidateDeploymentReadModels } from '@/lib/connect-query-invalidation'
import { formatTimestamp } from '@/lib/protobuf-utils'
import { useMutationWithToast } from '@/hooks/useMutationWithToast'
import { TagTrackingMode } from '@/gen/catpocs/v1/deployments_pb'

interface DeploymentTagListProps {
  applicationId: string
  currentTag?: string
  trackedTag?: string
  trackingEnabled?: boolean
  trackingMode?: string
}

function formatBytes(bytes: number | bigint | undefined): string {
  if (!bytes) return ''
  const b = typeof bytes === 'bigint' ? Number(bytes) : bytes
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export function DeploymentTagList({
  applicationId,
  currentTag,
  trackedTag,
  trackingEnabled,
  trackingMode,
}: DeploymentTagListProps) {
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false)
  const [selectedTag, setSelectedTag] = useState<string | null>(null)
  const [trackingTagSelection, setTrackingTagSelection] = useState<string>(trackedTag || currentTag || '')
  const [trackingEnabledSelection, setTrackingEnabledSelection] = useState<boolean>(!!trackingEnabled)
  const [trackingModeSelection, setTrackingModeSelection] = useState<'notify-only' | 'auto-deploy'>(
    trackingMode === 'auto-deploy' ? 'auto-deploy' : 'notify-only',
  )

  const { data: tagsResponse, isLoading, error, refetch } = useConnectQuery(
    listImageTags,
    { applicationId },
    {
      refetchInterval: 60000,
    }
  )

  const tags = tagsResponse?.tags

  useEffect(() => {
    setTrackingTagSelection(trackedTag || currentTag || '')
    setTrackingEnabledSelection(!!trackingEnabled)
    setTrackingModeSelection(trackingMode === 'auto-deploy' ? 'auto-deploy' : 'notify-only')
  }, [trackedTag, currentTag, trackingEnabled, trackingMode])

  const deployMutation = useMutationWithToast(deployImageTagRPC, {
    successTitle: 'Deployment initiated',
    successMessage: () => `Image tag ${selectedTag} is being deployed`,
    errorTitle: 'Deployment failed',
    invalidate: invalidateDeploymentReadModels,
    onSuccess: () => {
      setSelectedTag(null)
    },
  })

  const configureTrackingMutation = useMutationWithToast(configureTagTracking, {
    successTitle: 'Tag tracking updated',
    successMessage: 'The watched tag configuration has been saved.',
    errorTitle: 'Failed to update tag tracking',
    invalidate: invalidateDeploymentReadModels,
    onSuccess: () => {
      setIsTrackingModalOpen(false)
    },
  })

  const trackingModeProto = trackingModeSelection === 'auto-deploy'
    ? TagTrackingMode.AUTO_DEPLOY
    : TagTrackingMode.NOTIFY_ONLY

  const effectiveTrackedTag = trackedTag || currentTag || ''
  const effectiveTrackingMode = trackingMode === 'auto-deploy' ? 'auto-deploy' : 'notify-only'

  const trackingDirty = trackingTagSelection !== effectiveTrackedTag
    || trackingEnabledSelection !== !!trackingEnabled
    || trackingModeSelection !== effectiveTrackingMode

  const trackingOptions = useMemo(
    () => (tags || []).map((tag) => ({ value: tag.tag, label: tag.tag })),
    [tags],
  )

  const handleDeploy = (tag: string) => {
    setSelectedTag(tag)
    deployMutation.mutate({ applicationId, imageTag: tag })
  }

  const handleSaveTracking = () => {
    configureTrackingMutation.mutate({
      applicationId,
      tag: trackingTagSelection,
      enabled: trackingEnabledSelection,
      mode: trackingModeProto,
      imageDigestPath: '',
    })
  }

  const openTrackingModal = () => {
    setTrackingTagSelection(effectiveTrackedTag)
    setTrackingEnabledSelection(!!trackingEnabled)
    setTrackingModeSelection(effectiveTrackingMode)
    setIsTrackingModalOpen(true)
  }

  if (isLoading) {
    return (
      <Card>
        <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
          <Flex align="center" gap="sm">
            <Package size={20} />
            <Heading size="sub-section">Available Image Tags</Heading>
          </Flex>
          <Flex align="center" justify="center" style={{ padding: 32 }}>
            <Spinner size="lg" />
          </Flex>
        </Flex>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
          <Flex align="center" gap="sm">
            <Package size={20} />
            <Heading size="sub-section">Available Image Tags</Heading>
          </Flex>
          <Flex direction="vertical" align="center" gap="md" style={{ padding: 32 }}>
            <WarningCircle size={32} style={{ color: 'var(--mds-color-theme-text-error-normal)' }} />
            <Text size="sm" color="light">
              {error instanceof Error ? error.message : 'Failed to load image tags'}
            </Text>
            <Button kind="secondary" size="sm" onClick={() => refetch()} icon={<ArrowsClockwise size={16} />}>
              Retry
            </Button>
          </Flex>
        </Flex>
      </Card>
    )
  }

  if (!tags || tags.length === 0) {
    return (
      <Card>
        <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
          <Flex align="center" gap="sm">
            <Package size={20} />
            <Heading size="sub-section">Available Image Tags</Heading>
          </Flex>
          <Flex direction="vertical" align="center" style={{ padding: 32, opacity: 0.5 }}>
            <Package size={32} />
            <Text size="sm">No image tags available</Text>
          </Flex>
        </Flex>
      </Card>
    )
  }

  return (
    <Card>
      <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
        <Flex align="center" justify="space-between">
          <Flex align="center" gap="sm">
            <Package size={20} />
            <Heading size="sub-section">Available Image Tags</Heading>
          </Flex>
          <Tooltip title="Refresh tags">
            <Button kind="tertiary" size="sm" onClick={() => refetch()} icon={<ArrowsClockwise size={16} />} />
          </Tooltip>
        </Flex>

        <Card>
          <Flex direction="vertical" gap="sm" style={{ padding: 12 }}>
            <Flex align="center" justify="space-between">
              <Heading size="sub-section">Tag Tracking</Heading>
              <Button kind="secondary" size="sm" onClick={openTrackingModal}>
                Edit Tracking
              </Button>
            </Flex>
            <Text size="xs" style={{ opacity: 0.8 }}>
              Status: {trackingEnabled ? 'Enabled' : 'Disabled'}
            </Text>
            {trackingEnabled && effectiveTrackedTag && (
              <Text size="xs" style={{ opacity: 0.8 }}>
                Watching <code>{effectiveTrackedTag}</code> ({effectiveTrackingMode === 'auto-deploy' ? 'auto-deploy' : 'notify only'})
              </Text>
            )}
          </Flex>
        </Card>

        <Flex direction="vertical" gap="sm" style={{ maxHeight: 384, overflowY: 'auto' }}>
          {tags.map((tag) => {
            const isCurrentTag = tag.tag === currentTag
            const isTrackedTag = !!trackingEnabled && tag.tag === effectiveTrackedTag
            const isDeploying = deployMutation.isPending && selectedTag === tag.tag

            return (
              <Flex
                key={tag.tag}
                align="center"
                justify="space-between"
                style={{
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid var(--mds-color-theme-outline-secondary-normal)',
                }}
              >
                <Flex direction="vertical" gap="xs" style={{ flex: 1, minWidth: 0 }}>
                  <Flex align="center" gap="sm">
                    <Tag>{tag.tag}</Tag>
                    {isCurrentTag && (
                      <Tag.Status status="positive">Current</Tag.Status>
                    )}
                    {isTrackedTag && (
                      <Tag.Status status="info">Tracking</Tag.Status>
                    )}
                  </Flex>
                  <Flex align="center" gap="md" style={{ opacity: 0.7 }}>
                    <Flex align="center" gap="xs">
                      <Calendar size={12} />
                      <Text size="xs">
                        First seen: {tag.firstSeenAt ? formatTimestamp(tag.firstSeenAt) : 'Unknown'}
                      </Text>
                    </Flex>
                    <Flex align="center" gap="xs">
                      <Clock size={12} />
                      <Text size="xs">
                        Updated: {tag.updatedAt ? formatTimestamp(tag.updatedAt) : 'Unknown'}
                      </Text>
                    </Flex>
                    {tag.sizeBytes ? (
                      <Flex align="center" gap="xs">
                        <Download size={12} />
                        <Text size="xs">{formatBytes(tag.sizeBytes)}</Text>
                      </Flex>
                    ) : null}
                    {tag.digest && (
                      <Tag>{tag.digest.split(':')[1]?.slice(0, 12)}</Tag>
                    )}
                  </Flex>
                </Flex>
                <Button
                  size="sm"
                  onClick={() => handleDeploy(tag.tag)}
                  disabled={isCurrentTag || isDeploying || deployMutation.isPending}
                  icon={isDeploying ? <Spinner size="sm" /> : undefined}
                >
                  {isDeploying ? 'Deploying' : 'Deploy'}
                </Button>
              </Flex>
            )
          })}
        </Flex>
      </Flex>
      <Modal
        isOpen={isTrackingModalOpen}
        onClose={() => setIsTrackingModalOpen(false)}
        size="sm"
      >
        <Modal.Content>
          <Flex direction="vertical" gap="md" style={{ padding: 24 }}>
            <Heading size="section">Edit Tag Tracking</Heading>
            <Checkbox
              checked={trackingEnabledSelection}
              onClick={() => setTrackingEnabledSelection((prev) => !prev)}
            >
              Enable tracked-tag watcher
            </Checkbox>
            <Flex style={{ minWidth: 220 }}>
              <Select
                placeholder="Select tracked tag"
                value={trackingTagSelection}
                onChange={(value) => setTrackingTagSelection((value as string) || '')}
                options={trackingOptions}
              />
            </Flex>
            <Flex style={{ minWidth: 220 }}>
              <Select
                value={trackingModeSelection}
                onChange={(value) => setTrackingModeSelection((value as 'notify-only' | 'auto-deploy') || 'notify-only')}
                options={[
                  { value: 'notify-only', label: 'Notify only' },
                  { value: 'auto-deploy', label: 'Auto-deploy updates' },
                ]}
              />
            </Flex>
            <Flex justify="flex-end" gap="sm">
              <Button
                size="sm"
                kind="secondary"
                onClick={() => setIsTrackingModalOpen(false)}
                disabled={configureTrackingMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                kind="primary"
                disabled={
                  configureTrackingMutation.isPending
                  || !trackingDirty
                  || (trackingEnabledSelection && !trackingTagSelection)
                }
                onClick={handleSaveTracking}
                icon={configureTrackingMutation.isPending ? <Spinner size="sm" /> : undefined}
              >
                {configureTrackingMutation.isPending ? 'Saving' : 'Save Tracking'}
              </Button>
            </Flex>
          </Flex>
        </Modal.Content>
      </Modal>
    </Card>
  )
}
